import {
  RECORD_ENTITIES,
  RECORD_TEMPLATE_ENTITY,
  TEMPLATE_FIELDS,
  TEMPLATE_FIELD_TYPES,
  formatFieldValue,
  renderTemplate,
  type FieldFormat,
  type FieldType,
  type RecordEntity,
  type TemplateBundle,
} from '@crm/contracts';
import { api } from './client.ts';

/**
 * Merge fields in notes: filled in the browser, when a field or a Note template is inserted,
 * from the record the note goes on as the API serves it to the writer. A custom field hidden from the writer's role is not in that answer, so it fills empty;
 * no new endpoint reads anything the writer could not already see.
 */

type Values = Record<string, unknown>;

/** The record the note goes on, by its kind's own read. */
export function fetchRecordValues(entityType: RecordEntity, id: string): Promise<Values> {
  const read: Record<RecordEntity, (id: string) => Promise<unknown>> = {
    candidates: api.candidates.get,
    companies: api.companies.get,
    contacts: api.contacts.get,
    projects: api.projects.get,
    deals: api.deals.get,
    jobs: api.jobs.get,
  };
  return read[entityType](id) as Promise<Values>;
}

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** What `{{record.name}}` reads: the record's name as its page shows it. */
export function recordName(entityType: RecordEntity, r: Values): string {
  if (entityType === 'candidates' || entityType === 'contacts')
    return [text(r['firstName']), text(r['lastName'])].filter(Boolean).join(' ');
  if (entityType === 'deals' || entityType === 'jobs') return text(r['title']);
  return text(r['name']);
}

const DATE = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });
const DATE_TIME = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' });

/** How a merge field's value is read: its type, and for a custom field its definition's details. */
export interface FieldInfo {
  type: FieldType;
  options?: ReadonlyArray<{ value: string; label: string }> | undefined;
  format?: FieldFormat | null | undefined;
  target?: string | undefined;
}

/** What turning ids into names needs: the organisation's members and a record's name by id. */
export interface MergeContext {
  definitions: ReadonlyArray<FieldInfo & { key: string }>;
  userName: (id: string) => string | undefined;
  recordName: (kind: RecordEntity, id: string) => Promise<string>;
}

/** A stored code (`placement_fee`) as words; any other text as it is. */
const readable = (v: string) =>
  /^[a-z][a-z\d]*(_[a-z\d]+)*$/.test(v)
    ? v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ')
    : v;

const asText = (v: unknown): string =>
  typeof v === 'string'
    ? v
    : typeof v === 'number' || typeof v === 'boolean'
      ? String(v)
      : (JSON.stringify(v) ?? '');

/** One value as a person reads it on the record's page: names, labels, local dates. */
export function formatMergeValue(
  info: FieldInfo,
  value: unknown,
  userName: MergeContext['userName'],
): string {
  if (value === null || value === undefined || value === '') return '';
  const label = (v: string) => info.options?.find((o) => o.value === v)?.label ?? readable(v);
  switch (info.type) {
    case 'number': {
      const n = Number(value);
      return Number.isFinite(n) ? formatFieldValue(n, info.format) : asText(value);
    }
    case 'currency': {
      const c = value as { amountMinor?: unknown; currency?: unknown };
      if (typeof c.amountMinor !== 'number') return '';
      const code =
        typeof c.currency === 'string' && /^[A-Z]{3}$/.test(c.currency) ? c.currency : 'USD';
      const decimals = info.format?.decimals;
      const amount = (c.amountMinor / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: code,
        ...(decimals === undefined
          ? {}
          : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
      });
      return formatFieldValue(amount, info.format);
    }
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'date': {
      // A calendar day, not an instant: read in local time so it never shifts a day.
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(asText(value));
      return m
        ? DATE.format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
        : asText(value);
    }
    case 'datetime': {
      const d = new Date(asText(value));
      return Number.isNaN(d.getTime()) ? asText(value) : DATE_TIME.format(d);
    }
    case 'single_select':
      return label(asText(value));
    case 'multi_select':
      return (Array.isArray(value) ? value : [value]).map((v) => label(asText(v))).join(', ');
    case 'user':
      return userName(asText(value)) ?? '';
    default:
      return asText(value);
  }
}

const isRecordEntity = (v: string | undefined): v is RecordEntity =>
  (RECORD_ENTITIES as readonly string[]).includes(v ?? '');

/**
 * The record's values as a note shows them (`formatMergeValue`). A custom `reference` field
 * becomes the name of the record it points at, read only when `used` names it; one the writer
 * cannot read fills empty, like a hidden field.
 */
export async function mergeValues(
  kind: RecordEntity,
  record: Values,
  ctx: MergeContext,
  used: readonly string[],
): Promise<Values> {
  const entity = RECORD_TEMPLATE_ENTITY[kind];
  const values: Values = Object.fromEntries(
    Object.entries(TEMPLATE_FIELD_TYPES[entity]).map(([key, type]) => [
      key,
      formatMergeValue({ type }, record[key], ctx.userName),
    ]),
  );
  const defs = new Map(ctx.definitions.map((d) => [d.key, d]));
  const custom = (record['custom'] ?? {}) as Values;
  values['custom'] = Object.fromEntries(
    await Promise.all(
      Object.entries(custom).map(async ([key, v]) => {
        const def = defs.get(key);
        if (!def) return [key, v === null || v === undefined ? '' : asText(v)];
        if (def.type !== 'reference' || !isRecordEntity(def.target))
          return [key, formatMergeValue(def, v, ctx.userName)];
        if (!used.includes(`${entity}.custom.${key}`) || typeof v !== 'string' || !v)
          return [key, ''];
        const name = await ctx.recordName(def.target, v).catch((err: unknown) => {
          console.warn('merge field: the record a reference points at could not be read', err);
          return '';
        });
        return [key, name];
      }),
    ),
  );
  return values;
}

/**
 * The values a note on that record can name: its own fields as `mergeValues` shows them
 * (`custom` holds its custom ones) and the context fields.
 */
export function noteBundle(
  entityType: RecordEntity,
  record: Values,
  values: Values,
  me: { name: string },
  today = new Date(),
): TemplateBundle {
  return {
    [RECORD_TEMPLATE_ENTITY[entityType]]: values,
    record: { name: recordName(entityType, record) },
    me: { name: me.name },
    today: { date: today.toLocaleDateString('en-US', { dateStyle: 'medium' }) },
  } as TemplateBundle;
}

/** A template's (or one merge field's) HTML with every merge field filled, values escaped. */
export const fillNoteHtml = (html: string, bundle: TemplateBundle) =>
  renderTemplate(html, bundle, { html: true }).text;

/** `interviewDateTime` → "Interview date time". */
export const humanise = (key: string) => {
  const words = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export interface MergeFieldGroup {
  label: string;
  fields: Array<{ path: string; label: string }>;
  /** The record kind's custom fields, folded inside its group. */
  custom?: Array<{ path: string; label: string }>;
}

/** The context fields every note can use. */
export const CONTEXT_GROUP: MergeFieldGroup = {
  label: 'This note',
  fields: [
    { path: 'record.name', label: "Record's name" },
    { path: 'me.name', label: 'My name' },
    { path: 'today.date', label: "Today's date" },
  ],
};

/** A record kind's merge fields: its catalogue, then its custom fields. */
export function kindGroup(
  kind: RecordEntity,
  label: string,
  custom: ReadonlyArray<{ key: string; label: string; type: string }>,
): MergeFieldGroup {
  const entity = RECORD_TEMPLATE_ENTITY[kind];
  return {
    label,
    fields: TEMPLATE_FIELDS[entity]
      // Ids read as nothing to a person, and a job's whole description is no field to insert.
      .filter((k) => k !== 'id' && !k.endsWith('Id') && !(entity === 'job' && k === 'content'))
      .map((k) => ({ path: `${entity}.${k}`, label: humanise(k) })),
    custom: custom
      .filter((d) => d.type !== 'json')
      .map((d) => ({ path: `${entity}.custom.${d.key}`, label: d.label })),
  };
}
